/**
 * UsersPage - gestion des utilisateurs secondaires du tenant.
 * Reserve aux plans Business+. Si plan inferieur : upsell.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Title2, Caption1, Button, Badge, Input,
  Field, Dropdown, Option,
  Spinner, MessageBar, MessageBarBody,
  Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogContent,
  DialogBody, DialogActions, tokens,
} from '@fluentui/react-components';
import {
  PersonAdd24Regular, Delete20Regular, Sparkle24Filled,
  KeyMultiple20Regular, Info20Regular,
} from '@fluentui/react-icons';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { useLicense } from '../auth/LicenseGate';
import { tenantUsersApi, type TenantUser, type UserRole } from '../api/tenantUsersApi';

// Multi-utilisateurs : reserve Pro+ (cf. pricing matrix v2026.06)
const PRO_OR_HIGHER = new Set(['pro', 'business', 'enterprise']);

const ROLE_META: Record<UserRole, { color: 'brand' | 'success' | 'informative' }> = {
  admin:   { color: 'brand' },
  auditor: { color: 'success' },
  viewer:  { color: 'informative' },
};

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const license = useLicense();
  // Normalise lowercase pour tolerer les cles avec casse historique
  const plan = (license.status?.plan ?? '').toLowerCase();
  // Multi-utilisateurs Pro+ : on garde hasBusinessPlan comme nom local pour ne
  // pas toucher les passages internes. Semantique = "a au moins Pro".
  const hasBusinessPlan = Boolean(license.hasLicense && license.status?.valid && PRO_OR_HIGHER.has(plan));

  const [users, setUsers]     = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state - MVP : password obligatoire, role admin force
  const [showForm, setShowForm] = useState(false);
  const [fEmail, setFEmail]     = useState('');
  const [fName, setFName]       = useState('');
  // role est fige a 'admin' en MVP, mais on garde l etat au cas ou on relache plus tard
  const [fRole]                 = useState<UserRole>('admin');
  const [fPwd, setFPwd]         = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roleLabel = (r: UserRole) => t(`users.role.${r}`);

  async function reload() {
    if (!hasBusinessPlan) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await tenantUsersApi.list();
      setUsers(r.items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [hasBusinessPlan]);

  function resetForm() {
    setFEmail(''); setFName(''); setFPwd('');
    setError(null);
  }

  async function onCreate() {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        email:    fEmail.trim(),
        name:     fName.trim(),
        role:     fRole,
        password: fPwd,
        preferredLanguage: i18n.language,
      };
      const created = await tenantUsersApi.create(payload);
      // Affiche un message different selon que le mail est parti ou non
      if (created.mailSent === false) {
        setSuccess(t('users.mailFailedBody'));
      } else {
        setSuccess(t('users.mailSentBody', { email: fEmail }));
      }
      resetForm();
      setShowForm(false);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(u: TenantUser) {
    if (u.isCurrent) return;
    if (!confirm(t('users.confirmDelete', { email: u.email }))) return;
    setError(null);
    setSuccess(null);
    try {
      await tenantUsersApi.remove(u.id);
      setSuccess(t('users.deleteOk', { email: u.email }));
      await reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // -----------------------------------------------------------------------
  // Upsell si pas Business+
  // -----------------------------------------------------------------------
  if (!hasBusinessPlan) {
    return (
      <div>
        <PageHeader
          title={t('users.title')}
          description={t('users.subtitleUpsell')}
        />
        <Card style={{
          background: 'linear-gradient(135deg, #FFF3D6 0%, #FFE8B5 100%)',
          border: '1.5px solid #D9842B',
          padding: 28,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 16 }}>
            <Sparkle24Filled style={{ color: '#B45309', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <Title2
                style={{ color: '#8A6308', margin: 0 }}
                dangerouslySetInnerHTML={{ __html: t('users.upsell.titleHtml') }}
              />
              <Caption1
                style={{ color: '#8A6308', display: 'block', marginTop: 6 }}
                dangerouslySetInnerHTML={{
                  __html: t('users.upsell.currentPlan', { plan: plan || t('users.upsell.planTrial') }),
                }}
              />
            </div>
            <Link
              to="/billing"
              style={{
                background: '#1F3D5C', color: '#fff',
                padding: '10px 18px', borderRadius: 8,
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t('users.upsell.cta')}
            </Link>
          </div>
          <MessageBar intent="info" style={{ background: 'rgba(255,255,255,.6)' }}>
            <MessageBarBody style={{ fontSize: 13 }}>
              {t('users.upsell.detail')}
            </MessageBarBody>
          </MessageBar>
        </Card>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Mode Business+ : la vraie page
  // -----------------------------------------------------------------------
  return (
    <div>
      <PageHeader
        title={t('users.title')}
        description={t('users.subtitleActive')}
        actions={
          <Button
            appearance="primary"
            icon={<PersonAdd24Regular />}
            onClick={() => { resetForm(); setShowForm(true); }}
          >
            {t('users.newUser')}
          </Button>
        }
      />

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {success && (
        <MessageBar intent="success" style={{ marginBottom: 16 }}>
          <MessageBarBody>{success}</MessageBarBody>
        </MessageBar>
      )}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Spinner label={t('users.loading')} />
        </div>
      ) : (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, textAlign: 'left' }}>
                  <th style={thStyle}>{t('users.table.email')}</th>
                  <th style={thStyle}>{t('users.table.name')}</th>
                  <th style={thStyle}>{t('users.table.role')}</th>
                  <th style={thStyle}>{t('users.table.status')}</th>
                  <th style={thStyle}>{t('users.table.auth')}</th>
                  <th style={thStyle}>{t('users.table.lastLogin')}</th>
                  <th style={thStyle}>{t('users.table.createdAt')}</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
                    <td style={tdStyle}>
                      {u.email}
                      {u.isCurrent && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, padding: '2px 6px',
                          background: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1,
                          borderRadius: 999, fontWeight: 700,
                        }}>{t('users.you')}</span>
                      )}
                    </td>
                    <td style={tdStyle}>{u.name}</td>
                    <td style={tdStyle}>
                      <Badge color={ROLE_META[u.role].color} appearance="tint">
                        {roleLabel(u.role)}
                      </Badge>
                    </td>
                    <td style={tdStyle}>
                      <Badge
                        color={
                          u.status === 'active'  ? 'success' :
                          u.status === 'pending' ? 'warning' :
                                                    'severe'
                        }
                        appearance="filled"
                        title={u.status === 'pending' ? t('users.pendingHint') : undefined}
                      >
                        {t(`users.status.${u.status}`, { defaultValue: u.status })}
                      </Badge>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        {u.hasPassword
                          ? <Badge color="brand" appearance="tint">{t('users.authPassword')}</Badge>
                          : <Badge color="informative" appearance="tint">{t('users.authMagicLink')}</Badge>}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString(i18n.language) : '—'}
                    </td>
                    <td style={tdStyle}>{new Date(u.createdAt).toLocaleDateString(i18n.language)}</td>
                    <td style={tdStyle}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Delete20Regular />}
                        disabled={u.isCurrent}
                        onClick={() => onDelete(u)}
                        title={u.isCurrent ? t('users.deleteSelf') : t('users.deleteAction')}
                      />
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: tokens.colorNeutralForeground3, padding: 32 }}>
                      {t('users.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ============================================================ */}
      {/* Modal création utilisateur                                    */}
      {/* ============================================================ */}
      <Dialog open={showForm} onOpenChange={(_, d) => setShowForm(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('users.form.title')}</DialogTitle>
            <DialogContent>
              {/* Banniere MVP : explique limites actuelles */}
              <MessageBar intent="info" style={{ marginTop: 4, marginBottom: 12 }}>
                <MessageBarBody>
                  <Info20Regular style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {t('users.form.mvpBanner')}
                </MessageBarBody>
              </MessageBar>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 }}>
                <Field label={t('users.form.email')}>
                  <Input
                    type="email"
                    value={fEmail}
                    onChange={(_, d) => setFEmail(d.value)}
                    placeholder={t('users.form.emailPlaceholder')}
                  />
                </Field>
                <Field label={t('users.form.name')}>
                  <Input
                    value={fName}
                    onChange={(_, d) => setFName(d.value)}
                    placeholder={t('users.form.namePlaceholder')}
                  />
                </Field>

                {/* Role - admin force, auditor/viewer grises avec libelle "bientot" */}
                <Field label={t('users.form.role')} hint={t('users.form.adminOnlyNote')}>
                  <Dropdown
                    value={roleLabel('admin')}
                    selectedOptions={['admin']}
                    disabled
                  >
                    <Option value="admin">{t('users.role.adminDesc')}</Option>
                    <Option value="auditor" disabled>{t('users.role.auditorDesc')}</Option>
                    <Option value="viewer"  disabled>{t('users.role.viewerDesc')}</Option>
                  </Dropdown>
                </Field>

                {/* Password obligatoire - pas de switch */}
                <Field
                  label={t('users.form.password')}
                  hint={t('users.form.passwordRequiredHint')}
                  required
                >
                  <Input
                    type="password"
                    value={fPwd}
                    onChange={(_, d) => setFPwd(d.value)}
                    placeholder={t('users.form.password')}
                    contentBefore={<KeyMultiple20Regular />}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" onClick={() => setShowForm(false)}>
                  {t('users.form.cancel')}
                </Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={onCreate}
                disabled={!fEmail || !fName || !fPwd || fPwd.length < 10 || submitting}
              >
                {submitting ? t('users.form.submitting') : t('users.form.submit')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: tokens.colorNeutralForeground3,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
};
